import { createContext, useCallback, useEffect, useState } from "react";
import { Alert } from "reactstrap";

export const defaultAlertContextValue: IAlertContextValue = {
    newAlert: (context: Partial<IAlert>): void => {},
};

export const AlertContext = createContext<IAlertContextValue>(
    defaultAlertContextValue
);

/** @component Higher order component component abstracting AlertContext out of App.tsx This wraps the
 * component tree in App.tsx so that alertContext state and its state-setting callback
 * are available anywhere in the app.
 * @see useAlertContext
 */
export default function AlertContextProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    // State to handle alert messages will be stored in context and passed down component tree
    const [alerts, setAlerts] = useState<IAlertContext>([]);
    const [currentAlertIndex, setCurrentAlertIndex] = useState<number>(-1);

    /** Used to pop up an alert by editing state in AlertContext.
     *
     * @param alert An object that needs an isOpen boolean, message string, and a Bootstrap color string
     * Requires a message property. Color is optional and defaults to danger.
     * @see Bootstrap colors: https://getbootstrap.com/docs/5.1/customize/color/
     * @see useAlertContext
     * @example newAlert({message: "Test alert", color?: "warning"})
     */
    const newAlert = useCallback((alert: Partial<IAlert>) => {
        let indexDiff = 1;

        setAlerts((prev) => {
            // Prevent duplicate alerts from being added
            if (prev.some((a) => a.message === alert.message)) {
                indexDiff = 0;
            }
            return [
                ...prev.filter((a) => a.message !== alert.message),
                {
                    color: alert.color ?? "danger",
                    message: alert.message ?? "",
                    isOpen: alert.isOpen ?? true,
                    seconds: alert.seconds ?? 7,
                },
            ];
        });
        setCurrentAlertIndex((i) => i + indexDiff);
    }, []);

    // Dismiss alerts after timeout, switch to new alert from existing alert
    useEffect(() => {
        const currentAlert: IAlert = alerts[currentAlertIndex];
        if (!currentAlert || !currentAlert.isOpen) return;

        const timeout = setTimeout(() => {
            setAlerts((alerts) =>
                alerts.filter((alert, index) => index !== currentAlertIndex)
            );
            setCurrentAlertIndex((i) => i - 1);
        }, currentAlert.seconds * 1000);

        return () => clearTimeout(timeout);
    }, [alerts, currentAlertIndex, newAlert]);

    const currentAlert = alerts[currentAlertIndex];

    return (
        <AlertContext.Provider value={{ newAlert }}>
            {currentAlert && (
                <Alert
                    className="dimensionAlert"
                    color={currentAlert.color}
                    isOpen={currentAlert.isOpen}
                    toggle={() => {
                        setAlerts((alerts) =>
                            alerts.filter(
                                (alert, index) => index !== currentAlertIndex
                            )
                        );
                        setCurrentAlertIndex((i) => i - 1);
                    }}
                >
                    {currentAlert.message}
                </Alert>
            )}
            {children}
        </AlertContext.Provider>
    );
}
